import { useState, useEffect, useMemo } from "react";
import { supa } from "../components/firebase";
import { Calendar, Wallet, User, Group, Trophy, StatsUpSquare, Refresh } from 'iconoir-react'
import { useParams, useNavigate } from "react-router-dom";
import ActivityIndicator from "../components/activityIndicator";
import { Chart as ChartJS, ArcElement, CategoryScale, LinearScale, PointElement, LineElement, BarElement, Title, Tooltip, Legend } from 'chart.js';
import { Line, Bar } from 'react-chartjs-2';

ChartJS.register(ArcElement, CategoryScale, LinearScale, PointElement, LineElement, BarElement, Title, Tooltip, Legend);

function ProfiloPersonalTrainer() {
    const navigate = useNavigate();
    const { id } = useParams();
    const [personalTrainer, setPersonalTrainer] = useState({});
    
    const [nome, setNome] = useState("");
    const [cognome, setCognome] = useState("");
    const [costo, setCosto] = useState(0);
    const [specializzazione, setSpecializzazione] = useState("");

    const [lezioniOriginali, setLezioniOriginali] = useState([]);
    const [lezioni, setLezioni] = useState([]);
    
    // Filtri data
    const [dal, setDal] = useState("");
    const [al, setAl] = useState("");

    async function getPersonalTrainer() {
        const { data, error } = await supa
            .from('personalTrainer')
            .select('*')
            .eq('id', id)
            .single();
        if (error) console.log("---", error);
        setPersonalTrainer(data);
        setNome(data.nome);
        setCognome(data.cognome);
        setCosto(data.costo || 0);
        setSpecializzazione(data.personale ? "Personal Training" : "Pilates");
    }

    async function getLezioni() {
        console.log("🔄 getLezioni() chiamata per PT ID:", id, "- Timestamp:", new Date().toISOString());
        
        try {
            // PAGINAZIONE: Supabase ha un limite di 1000 righe per query
            // Faccio più chiamate finché non ho caricato tutte le lezioni
            let allData = [];
            let from = 0;
            const pageSize = 1000;
            let hasMore = true;
            
            while (hasMore) {
                const { data, error, count } = await supa
                    .from('lezioni')
                    .select('*,utenti(*),personalTrainer(*),pacchettiAcquistati(*,pacchetti(*),versamenti(*))', { count: 'exact' })
                    .eq('id_personalTrainer', id)
                    .order('data', { ascending: true })
                    .range(from, from + pageSize - 1);
                
                if (error) {
                    console.error("❌ Errore caricamento lezioni:", error);
                    break;
                }
                
                console.log(`📦 Batch caricato: ${data.length} lezioni (da ${from} a ${from + pageSize - 1}). Totale DB: ${count}`);
                
                allData = [...allData, ...data];
                from += pageSize;
                hasMore = data.length === pageSize; // Se ho ricevuto 1000, potrebbero essercene altre
            }
            
            console.log("✅ TUTTE le lezioni caricate:", allData.length);
            
            setLezioniOriginali(allData);
            setLezioni(allData);
        } catch (err) {
            console.error("❌ Errore durante il caricamento:", err);
        }
    }

    function applicaFiltri() {
        if (!dal && !al) {
            setLezioni(lezioniOriginali);
            return;
        }

        const filtrate = lezioniOriginali.filter(l => {
            const dataLezione = new Date(l.data);
            const dataInizio = dal ? new Date(dal) : new Date('2000-01-01');
            const dataFine = al ? new Date(al) : new Date('2100-12-31');
            return dataLezione >= dataInizio && dataLezione <= dataFine;
        });
        
        setLezioni(filtrate);
    }

    async function patchPersonalTrainer() {
        const { data, error } = await supa
            .from('personalTrainer')
            .update({ "nome": nome, "cognome": cognome, "costo": costo })
            .eq('id', personalTrainer.id);
        if (error) console.log(error);
        navigate(-1);
    }

    async function alertPatch() {
        if (window.confirm(`Sei sicuro di voler salvare le modifiche?`)) {
            patchPersonalTrainer();
        }
    }

    async function alertDelete() {
        if (window.confirm(`Sei sicuro di voler eliminare il profilo del Personal Trainer?\nATTENZIONE: questa azione è irreversibile`)) {
            deletePersonalTrainer();
        }
    }

    async function deletePersonalTrainer() {
        const { data, error} = await supa
            .from('personalTrainer')
            .delete()
            .eq('id', personalTrainer.id);
        if (error) { 
            alert("Errore durante l'eliminazione del profilo\nProbabilmente ha lezioni collegate\nContattare GioPz se hai bisogno di assistenza");
        } else {
            navigate(-1);
        }
    }

    useEffect(() => {
        getPersonalTrainer();
    }, []);

    useEffect(() => {
        if (personalTrainer?.id) {
            getLezioni();
        }
    }, [personalTrainer.id]);

    // Applica automaticamente i filtri quando cambiano le date o i dati
    useEffect(() => {
        applicaFiltri();
    }, [dal, al, lezioniOriginali]);

    // Statistiche PT con calcoli economici avanzati
    const statistiche = useMemo(() => {
        if (!lezioni.length) return null;

        const oggi = new Date();
        oggi.setHours(0, 0, 0, 0); // Reset ore per confronti corretti
        
        console.log("=== DEBUG ProfiloPersonalTrainer ===");
        console.log("Data di oggi:", oggi.toLocaleDateString('it-IT'));
        console.log("ID Personal Trainer:", id);
        console.log("Nome PT:", nome, cognome);
        console.log("Costo PT per lezione:", costo);
        console.log("Totale lezioni array:", lezioni.length);
        console.log("Lezioni (prime 3 e ultime 3):", {
            prime3: lezioni.slice(0, 3).map(l => ({ id: l.id, data: l.data, utente: l.utenti?.nome })),
            ultime3: lezioni.slice(-3).map(l => ({ id: l.id, data: l.data, utente: l.utenti?.nome }))
        });
        console.log("lezioniOriginali length:", lezioniOriginali.length);

        // Calcoli economici
        const pacchettiUnici = new Map();
        lezioni.forEach(l => {
            const pacchettoId = l.pacchettiAcquistati?.id;
            if (pacchettoId && !pacchettiUnici.has(pacchettoId)) {
                pacchettiUnici.set(pacchettoId, l.pacchettiAcquistati);
            }
        });

        let incassoTotale = 0;
        pacchettiUnici.forEach(pacchetto => {
            const versamenti = pacchetto.versamenti || [];
            versamenti.forEach(v => {
                incassoTotale += v.importo || 0;
            });
        });

        const costoPT = lezioni.length * (costo || 0);
        const guadagnoNetto = incassoTotale - costoPT;
        const marginePercentuale = incassoTotale > 0 ? (guadagnoNetto / incassoTotale) * 100 : 0;

        // Andamento economico mensile (ultimi 6 mesi)
        const economicaMensile = [];
        for (let i = 5; i >= 0; i--) {
            const data = new Date();
            data.setMonth(data.getMonth() - i);
            
            const lezioniMese = lezioni.filter(l => {
                const dataLezione = new Date(l.data);
                return dataLezione.getMonth() === data.getMonth() && dataLezione.getFullYear() === data.getFullYear();
            });
            
            const pacchettiMese = new Map();
            lezioniMese.forEach(l => {
                const pacchettoId = l.pacchettiAcquistati?.id;
                if (pacchettoId && !pacchettiMese.has(pacchettoId)) {
                    pacchettiMese.set(pacchettoId, l.pacchettiAcquistati);
                }
            });
            
            let incassoMese = 0;
            pacchettiMese.forEach(pacchetto => {
                const versamenti = pacchetto.versamenti || [];
                versamenti.forEach(v => {
                    const dataVersamento = new Date(v.data);
                    if (dataVersamento.getMonth() === data.getMonth() && dataVersamento.getFullYear() === data.getFullYear()) {
                        incassoMese += v.importo || 0;
                    }
                });
            });
            
            const costoMese = lezioniMese.length * (costo || 0);
            const guadagnoMese = incassoMese - costoMese;
            
            economicaMensile.push({
                mese: data.toLocaleDateString('it-IT', { month: 'short', year: 'numeric' }),
                incasso: incassoMese,
                costo: costoMese,
                guadagno: guadagnoMese,
                lezioni: lezioniMese.length
            });
        }

        // Analisi clienti
        const analisiClienti = {};
        lezioni.forEach(l => {
            const utenteId = l.utenti?.id;
            const utenteNome = `${l.utenti?.nome} ${l.utenti?.cognome}`;
            
            if (!analisiClienti[utenteId]) {
                analisiClienti[utenteId] = {
                    id: utenteId,
                    nome: utenteNome,
                    lezioni: [],
                    totaleLezioni: 0,
                    primaLezione: l.data,
                    ultimaLezione: l.data
                };
            }
            
            analisiClienti[utenteId].lezioni.push(l.data);
            analisiClienti[utenteId].totaleLezioni++;
            
            if (new Date(l.data) < new Date(analisiClienti[utenteId].primaLezione)) {
                analisiClienti[utenteId].primaLezione = l.data;
            }
            if (new Date(l.data) > new Date(analisiClienti[utenteId].ultimaLezione)) {
                analisiClienti[utenteId].ultimaLezione = l.data;
            }
        });
        
        console.log("Analisi clienti (primi 2):", Object.values(analisiClienti).slice(0, 2));

        const clientiConFrequenza = Object.values(analisiClienti).map(cliente => {
            const giorniTotali = Math.max(1, Math.ceil((new Date(cliente.ultimaLezione) - new Date(cliente.primaLezione)) / (1000 * 60 * 60 * 24)));
            const settimane = Math.max(1, giorniTotali / 7);
            const mediaLezioniSettimana = cliente.totaleLezioni / settimane;
            
            const intervalli = [];
            const lezioniOrdinate = cliente.lezioni.sort((a, b) => new Date(a) - new Date(b));
            for (let i = 1; i < lezioniOrdinate.length; i++) {
                const giorniIntervallo = (new Date(lezioniOrdinate[i]) - new Date(lezioniOrdinate[i-1])) / (1000 * 60 * 60 * 24);
                intervalli.push(giorniIntervallo);
            }
            
            const mediaIntervallo = intervalli.length > 0 ? intervalli.reduce((a, b) => a + b, 0) / intervalli.length : 0;
            const varianza = intervalli.length > 1 ? intervalli.reduce((acc, val) => acc + Math.pow(val - mediaIntervallo, 2), 0) / intervalli.length : 0;
            const deviazioneStandard = Math.sqrt(varianza);
            const coefficienteVariazione = mediaIntervallo > 0 ? (deviazioneStandard / mediaIntervallo) * 100 : 0;
            
            // Normalizzo l'ultima lezione a mezzanotte per confronto corretto
            const dataUltimaLezione = new Date(cliente.ultimaLezione);
            dataUltimaLezione.setHours(0, 0, 0, 0);
            const giorniDallUltimaLezione = Math.floor((oggi - dataUltimaLezione) / (1000 * 60 * 60 * 24));
            const attivo = giorniDallUltimaLezione <= 30;
            
            return {
                ...cliente,
                mediaLezioniSettimana: mediaLezioniSettimana,
                mediaGiorniTraLezioni: mediaIntervallo,
                regolarita: coefficienteVariazione,
                regolaValore: coefficienteVariazione < 50 ? "Alta" : coefficienteVariazione < 100 ? "Media" : "Bassa",
                giorniDallUltima: giorniDallUltimaLezione,
                attivo: attivo
            };
        });

        const topClienti = clientiConFrequenza
            .sort((a, b) => b.totaleLezioni - a.totaleLezioni)
            .slice(0, 10);
        
        console.log("Top 3 clienti:", topClienti.slice(0, 3).map(c => {
            const ultLez = new Date(c.ultimaLezione);
            const giorniCalcolati = Math.floor((oggi - ultLez) / (1000 * 60 * 60 * 24));
            return {
                nome: c.nome,
                totaleLezioni: c.totaleLezioni,
                mediaLezioniSettimana: c.mediaLezioniSettimana,
                giorniDallUltima: c.giorniDallUltima,
                giorniCalcolatiManualmente: giorniCalcolati,
                ultimaLezione: c.ultimaLezione,
                ultimaLezioneDate: ultLez.toLocaleDateString('it-IT'),
                oggi: oggi.toLocaleDateString('it-IT'),
                attivo: c.attivo
            };
        }));

        const clientiAttivi = clientiConFrequenza.filter(c => c.attivo).length;
        const clientiInattivi = clientiConFrequenza.filter(c => !c.attivo).length;
        const mediaFrequenzaGenerale = clientiConFrequenza.length > 0 
            ? clientiConFrequenza.reduce((acc, c) => acc + c.mediaLezioniSettimana, 0) / clientiConFrequenza.length 
            : 0;
        const mediaLezioniPerCliente = clientiConFrequenza.length > 0 ? lezioni.length / clientiConFrequenza.length : 0;

        // Andamento settimanale
        // Calcolo settimane da lunedì a domenica
        const primaLezioneData = lezioni.length > 0 ? new Date(lezioni[0]?.data) : oggi;
        
        // Trovo il lunedì della settimana corrente
        const lunediCorrente = new Date(oggi);
        lunediCorrente.setHours(0, 0, 0, 0);
        const giorniDaLunedi = (lunediCorrente.getDay() + 6) % 7; // 0 = Lunedì, 6 = Domenica
        lunediCorrente.setDate(lunediCorrente.getDate() - giorniDaLunedi);
        
        const settimaneAttivitaTotali = Math.ceil((lunediCorrente - primaLezioneData) / (1000 * 60 * 60 * 24 * 7)) + 1;
        const numeroSettimaneGrafico = Math.min(26, settimaneAttivitaTotali);
        
        const tutteSettimane = [];
        for (let i = numeroSettimaneGrafico - 1; i >= 0; i--) {
            // Calcolo il lunedì della settimana i settimane fa
            const inizioSettimana = new Date(lunediCorrente);
            inizioSettimana.setDate(inizioSettimana.getDate() - (i * 7));
            
            // Domenica = lunedì + 6 giorni
            const fineSettimana = new Date(inizioSettimana);
            fineSettimana.setDate(fineSettimana.getDate() + 7); // Fino al lunedì successivo (escluso)
            
            const lezioniSettimana = lezioni.filter(l => {
                const dataLezione = new Date(l.data);
                dataLezione.setHours(0, 0, 0, 0);
                return dataLezione >= inizioSettimana && dataLezione < fineSettimana;
            });
            
            // Label con intervallo settimana (Lunedì - Domenica)
            const domenica = new Date(fineSettimana);
            domenica.setDate(domenica.getDate() - 1); // Ultimo giorno incluso (domenica)
            const inizioLabel = inizioSettimana.toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit' });
            const fineLabelStr = domenica.toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit' });
            
            tutteSettimane.push({
                settimana: `${inizioLabel}-${fineLabelStr}`,
                lezioni: lezioniSettimana.length
            });
        }

        // Trend performance
        const trentaGiorniFa = new Date(oggi);
        trentaGiorniFa.setDate(trentaGiorniFa.getDate() - 30);
        const sessantaGiorniFa = new Date(oggi);
        sessantaGiorniFa.setDate(sessantaGiorniFa.getDate() - 60);
        
        const lezioniUltimi30 = lezioni.filter(l => new Date(l.data) >= trentaGiorniFa).length;
        const lezioniPrecedenti30 = lezioni.filter(l => {
            const d = new Date(l.data);
            return d >= sessantaGiorniFa && d < trentaGiorniFa;
        }).length;
        
        const trendPercentuale = lezioniPrecedenti30 > 0 
            ? ((lezioniUltimi30 - lezioniPrecedenti30) / lezioniPrecedenti30) * 100 
            : (lezioniUltimi30 > 0 ? 100 : 0);

        // Trova la lezione più recente (ultima in ordine cronologico)
        let ultimaLezioneData = null;
        if (lezioni.length > 0) {
            // L'array è già ordinato per data crescente, quindi l'ultima è la più recente
            const ultimaLez = lezioni[lezioni.length - 1];
            ultimaLezioneData = new Date(ultimaLez.data);
            ultimaLezioneData.setHours(0, 0, 0, 0);
            
            console.log("Ultima lezione trovata:", {
                data: ultimaLez.data,
                utente: ultimaLez.utenti?.nome,
                dataObj: ultimaLezioneData.toLocaleDateString('it-IT')
            });
        }
        const giorniDallUltimaLezione = ultimaLezioneData ? Math.floor((oggi - ultimaLezioneData) / (1000 * 60 * 60 * 24)) : 0;

        const giorniAttivita = Math.max(1, Math.ceil((oggi - primaLezioneData) / (1000 * 60 * 60 * 24)));
        const settimaneAttivita = Math.max(1, giorniAttivita / 7);
        const mediaLezioniSettimana = lezioni.length / settimaneAttivita;
        
        console.log("RISULTATI FINALI:");
        console.log("- Totale lezioni:", lezioni.length);
        console.log("- Costo PT totale:", costoPT);
        console.log("- Incasso totale:", incassoTotale);
        console.log("- Clienti totali:", clientiConFrequenza.length);
        console.log("- Economica mensile:", economicaMensile);

        return {
            incassoTotale,
            costoPT,
            guadagnoNetto,
            marginePercentuale,
            economicaMensile,
            topClienti,
            clientiAttivi,
            clientiInattivi,
            totaleClienti: clientiConFrequenza.length,
            mediaFrequenzaGenerale,
            mediaLezioniPerCliente,
            clientiConFrequenza,
            totaleLezioni: lezioni.length,
            mediaLezioniSettimana,
            tutteSettimane,
            numeroSettimaneGrafico,
            trendPercentuale,
            lezioniUltimi30,
            lezioniPrecedenti30,
            giorniDallUltimaLezione,
            ultimaLezioneData
        };
    }, [lezioni, costo]);

    return (
        <div className="min-h-screen bg-gradient-to-br from-zinc-50 to-zinc-100 overflow-scroll noBar">
            {!personalTrainer?.id ? (
                <div className="flex items-center justify-center min-h-screen">
                    <ActivityIndicator />
                </div>
            ) : (
                <div className="w-full px-4 sm:px-6 lg:px-8 py-8 space-y-6">
                    <div className="bg-white rounded-2xl shadow-sm border border-zinc-200 p-6">
                        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
                            <div>
                                <h1 className="text-3xl font-bold text-zinc-900">Profilo Personal Trainer</h1>
                                <p className="mt-1 text-sm text-zinc-600">{nome} {cognome} - <span className="font-semibold text-blue-600">{specializzazione}</span></p>
                            </div>
                            
                            <div className="flex flex-wrap items-center gap-3 bg-zinc-50 rounded-xl p-4">
                                <span className="text-sm font-medium text-zinc-700">Dal</span>
                                <input 
                                    type="date" 
                                    value={dal} 
                                    onChange={(e) => setDal(e.target.value)}
                                    className="px-3 py-2 bg-white border border-zinc-300 rounded-lg text-sm focus:ring-2 focus:ring-yellow-400 focus:border-yellow-400"
                                />
                                <span className="text-sm font-medium text-zinc-700">al</span>
                                <input 
                                    type="date" 
                                    value={al} 
                                    onChange={(e) => setAl(e.target.value)}
                                    className="px-3 py-2 bg-white border border-zinc-300 rounded-lg text-sm focus:ring-2 focus:ring-yellow-400 focus:border-yellow-400"
                                />
                                <button 
                                    onClick={applicaFiltri}
                                    className="inline-flex items-center gap-2 px-4 py-2 bg-yellow-400 hover:bg-yellow-500 text-zinc-900 font-semibold rounded-lg text-sm shadow-sm hover:shadow transition-all"
                                >
                                    <Refresh className="w-4 h-4" />
                                    Applica
                                </button>
                                {(dal || al) && (
                                    <button 
                                        onClick={() => {
                                            setDal("");
                                            setAl("");
                                            setLezioni(lezioniOriginali);
                                        }}
                                        className="px-3 py-2 text-sm text-zinc-600 hover:text-zinc-900"
                                    >
                                        Reset
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="bg-white rounded-2xl shadow-sm border border-zinc-200 p-6">
                        <h2 className="text-xl font-semibold text-zinc-900 mb-4">Informazioni Personali</h2>
                        <div className="grid gap-4 grid-cols-1 md:grid-cols-3">
                            <div>
                                <label className="block mb-2 text-sm font-medium text-zinc-700">Nome</label>
                                <input 
                                    type="text" 
                                    onChange={(e) => setNome(e.target.value.toLocaleUpperCase())} 
                                    value={nome} 
                                    className="bg-white border border-zinc-300 text-zinc-900 text-sm rounded-xl focus:ring-2 focus:ring-blue-400 focus:border-blue-400 block w-full p-2.5 transition-all" 
                                    placeholder="Inserisci nome"
                                />
                            </div>
                            <div>
                                <label className="block mb-2 text-sm font-medium text-zinc-700">Cognome</label>
                                <input 
                                    type="text" 
                                    onChange={(e) => setCognome(e.target.value.toLocaleUpperCase())} 
                                    value={cognome} 
                                    className="bg-white border border-zinc-300 text-zinc-900 text-sm rounded-xl focus:ring-2 focus:ring-blue-400 focus:border-blue-400 block w-full p-2.5 transition-all" 
                                    placeholder="Inserisci cognome"
                                />
                            </div>
                            <div>
                                <label className="block mb-2 text-sm font-medium text-zinc-700">Costo per Lezione (€)</label>
                                <input 
                                    type="number" 
                                    onChange={(e) => setCosto(parseFloat(e.target.value))} 
                                    value={costo} 
                                    className="bg-white border border-zinc-300 text-zinc-900 text-sm rounded-xl focus:ring-2 focus:ring-blue-400 focus:border-blue-400 block w-full p-2.5 transition-all" 
                                    placeholder="Costo"
                                />
                            </div>
                        </div>
                    </div>

                    {statistiche && (
                        <>
                            {/* KPI Cards - Economici + Lezioni */}
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                                <div className="bg-white rounded-xl shadow-sm border border-zinc-200 p-5 hover:shadow-md transition-shadow">
                                    <div className="flex items-center justify-between mb-3">
                                        <Trophy className="w-8 h-8 text-yellow-500" />
                                        <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${
                                            statistiche.trendPercentuale >= 0 
                                                ? 'bg-green-100 text-green-700' 
                                                : 'bg-red-100 text-red-700'
                                        }`}>
                                            {statistiche.trendPercentuale >= 0 ? '+' : ''}{statistiche.trendPercentuale.toFixed(1)}%
                                        </span>
                                    </div>
                                    <p className="text-3xl font-bold text-zinc-900">{statistiche.totaleLezioni}</p>
                                    <p className="text-sm text-zinc-600 mt-1">Lezioni Totali</p>
                                    <p className="text-xs text-zinc-500 mt-2">
                                        {statistiche.lezioniUltimi30} ultimi 30gg
                                    </p>
                                </div>

                                <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl shadow-sm border border-green-200 p-5 hover:shadow-md transition-shadow">
                                    <div className="flex items-center justify-between mb-3">
                                        <Wallet className="w-8 h-8 text-green-600" />
                                        <span className="text-xs font-semibold text-green-700">INCASSO</span>
                                    </div>
                                    <p className="text-3xl font-bold text-green-700">{statistiche.incassoTotale.toFixed(0)} €</p>
                                    <p className="text-sm text-green-600 mt-1">Totale Incassato</p>
                                    <p className="text-xs text-green-600 mt-2">
                                        Da versamenti pacchetti
                                    </p>
                                </div>

                                <div className="bg-gradient-to-br from-red-50 to-red-100 rounded-xl shadow-sm border border-red-200 p-5 hover:shadow-md transition-shadow">
                                    <div className="flex items-center justify-between mb-3">
                                        <User className="w-8 h-8 text-red-600" />
                                        <span className="text-xs font-semibold text-red-700">COSTO</span>
                                    </div>
                                    <p className="text-3xl font-bold text-red-700">- {statistiche.costoPT.toFixed(0)} €</p>
                                    <p className="text-sm text-red-600 mt-1">Costo Personal Trainer</p>
                                    <p className="text-xs text-red-600 mt-2">
                                        {statistiche.totaleLezioni} × {costo}€
                                    </p>
                                </div>

                                <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl shadow-sm border border-blue-200 p-5 hover:shadow-md transition-shadow">
                                    <div className="flex items-center justify-between mb-3">
                                        <StatsUpSquare className="w-8 h-8 text-blue-600" />
                                        <span className="text-xs font-semibold text-blue-700">MARGINE {statistiche.marginePercentuale.toFixed(1)}%</span>
                                    </div>
                                    <p className={`text-3xl font-bold ${statistiche.guadagnoNetto >= 0 ? 'text-blue-700' : 'text-red-700'}`}>
                                        {statistiche.guadagnoNetto >= 0 ? '+' : ''}{statistiche.guadagnoNetto.toFixed(0)} €
                                    </p>
                                    <p className="text-sm text-blue-600 mt-1">Guadagno Netto</p>
                                    <p className="text-xs text-blue-600 mt-2">
                                        Incasso - Costo PT
                                    </p>
                                </div>
                            </div>

                            {/* Cards Frequenza Clienti */}
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <div className="bg-white rounded-xl shadow-sm border border-zinc-200 p-5">
                                    <div className="flex items-center justify-between mb-3">
                                        <Group className="w-8 h-8 text-purple-500" />
                                    </div>
                                    <p className="text-3xl font-bold text-zinc-900">{statistiche.totaleClienti}</p>
                                    <p className="text-sm text-zinc-600 mt-1">Clienti Totali</p>
                                    <div className="mt-3 flex gap-2">
                                        <span className="px-2 py-1 bg-green-100 text-green-700 text-xs font-semibold rounded">
                                            {statistiche.clientiAttivi} attivi
                                        </span>
                                        <span className="px-2 py-1 bg-zinc-100 text-zinc-700 text-xs font-semibold rounded">
                                            {statistiche.clientiInattivi} inattivi
                                        </span>
                                    </div>
                                </div>

                                <div className="bg-white rounded-xl shadow-sm border border-zinc-200 p-5">
                                    <div className="flex items-center justify-between mb-3">
                                        <Calendar className="w-8 h-8 text-blue-500" />
                                    </div>
                                    <p className="text-3xl font-bold text-zinc-900">{statistiche.mediaFrequenzaGenerale.toFixed(1)}</p>
                                    <p className="text-sm text-zinc-600 mt-1">Media Lezioni/Sett per Cliente</p>
                                    <p className="text-xs text-zinc-500 mt-2">
                                        Tasso frequenza medio
                                    </p>
                                </div>

                                <div className="bg-white rounded-xl shadow-sm border border-zinc-200 p-5">
                                    <div className="flex items-center justify-between mb-3">
                                        <Trophy className="w-8 h-8 text-orange-500" />
                                    </div>
                                    <p className="text-3xl font-bold text-zinc-900">{statistiche.mediaLezioniPerCliente.toFixed(1)}</p>
                                    <p className="text-sm text-zinc-600 mt-1">Media Lezioni per Cliente</p>
                                    <p className="text-xs text-zinc-500 mt-2">
                                        Totale lezioni / clienti
                                    </p>
                                </div>
                            </div>

                            {/* Grafici */}
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                                <div className="bg-white rounded-xl shadow-sm border border-zinc-200 p-6">
                                    <h3 className="text-lg font-semibold text-zinc-900 mb-4">💰 Andamento Economico (6 mesi)</h3>
                                    <div className="h-64">
                                        <Bar
                                            data={{
                                                labels: statistiche.economicaMensile.map(m => m.mese),
                                                datasets: [
                                                    {
                                                        label: 'Incasso (€)',
                                                        data: statistiche.economicaMensile.map(m => m.incasso),
                                                        backgroundColor: 'rgba(34, 197, 94, 0.8)',
                                                        borderRadius: 6,
                                                    },
                                                    {
                                                        label: 'Costo PT (€)',
                                                        data: statistiche.economicaMensile.map(m => m.costo),
                                                        backgroundColor: 'rgba(239, 68, 68, 0.8)',
                                                        borderRadius: 6,
                                                    }
                                                ]
                                            }}
                                            options={{
                                                responsive: true,
                                                maintainAspectRatio: false,
                                                plugins: {
                                                    legend: { 
                                                        position: 'top',
                                                        labels: {
                                                            usePointStyle: true,
                                                            pointStyle: 'circle',
                                                            padding: 15,
                                                            font: { size: 11 }
                                                        }
                                                    }
                                                },
                                                scales: {
                                                    y: { beginAtZero: true },
                                                    x: { grid: { display: false } }
                                                }
                                            }}
                                        />
                                    </div>
                                </div>

                                <div className="bg-white rounded-xl shadow-sm border border-zinc-200 p-6">
                                    <h3 className="text-lg font-semibold text-zinc-900 mb-4">
                                        📈 Lezioni Settimanali (ultimi 6 mesi)
                                    </h3>
                                    <p className="text-sm text-zinc-500 mb-3">
                                        Ogni punto rappresenta il totale di lezioni in una settimana (7 giorni)
                                    </p>
                                    <div className="h-64">
                                        <Line
                                            data={{
                                                labels: statistiche.tutteSettimane.map(s => s.settimana),
                                                datasets: [{
                                                    label: 'Lezioni settimanali',
                                                    data: statistiche.tutteSettimane.map(s => s.lezioni),
                                                    borderColor: 'rgb(250, 204, 21)',
                                                    backgroundColor: 'rgba(250, 204, 21, 0.1)',
                                                    tension: 0.4,
                                                    fill: true,
                                                    borderWidth: 2
                                                }]
                                            }}
                                            options={{
                                                responsive: true,
                                                maintainAspectRatio: false,
                                                plugins: {
                                                    legend: { display: false },
                                                    tooltip: {
                                                        callbacks: {
                                                            label: function(context) {
                                                                const lezioni = context.parsed.y;
                                                                const mediaGiornaliera = (lezioni / 7).toFixed(1);
                                                                return [
                                                                    `${lezioni} lezioni in questa settimana`,
                                                                    `Media: ${mediaGiornaliera} lezioni/giorno`
                                                                ];
                                                            }
                                                        }
                                                    }
                                                },
                                                scales: {
                                                    y: {
                                                        beginAtZero: true,
                                                        ticks: { 
                                                            stepSize: 5,
                                                            callback: function(value) {
                                                                return value + ' lez.';
                                                            }
                                                        }
                                                    },
                                                    x: {
                                                        ticks: {
                                                            maxRotation: 45,
                                                            minRotation: 45,
                                                            autoSkip: true,
                                                            maxTicksLimit: 12
                                                        }
                                                    }
                                                }
                                            }}
                                        />
                                    </div>
                                    <div className="mt-4 p-3 bg-yellow-50 rounded-lg">
                                        <p className="text-sm text-zinc-700">
                                            <span className="font-semibold">Media:</span> {statistiche.mediaLezioniSettimana.toFixed(1)} lezioni/settimana
                                        </p>
                                    </div>
                                </div>
                            </div>

                            {/* Top 10 Clienti - Tabella Frequenza */}
                            <div className="bg-white rounded-xl shadow-sm border border-zinc-200 overflow-hidden">
                                <div className="px-6 py-5 border-b border-zinc-200">
                                    <h3 className="text-lg font-semibold text-zinc-900">🏆 Top 10 Clienti - Analisi Frequenza</h3>
                                </div>
                                <div className="overflow-x-auto">
                                    <table className="w-full">
                                        <thead className="bg-zinc-50 border-b border-zinc-200">
                                            <tr>
                                                <th className="px-6 py-3 text-left text-xs font-semibold text-zinc-700 uppercase">#</th>
                                                <th className="px-6 py-3 text-left text-xs font-semibold text-zinc-700 uppercase">Cliente</th>
                                                <th className="px-6 py-3 text-left text-xs font-semibold text-zinc-700 uppercase">Lezioni</th>
                                                <th className="px-6 py-3 text-left text-xs font-semibold text-zinc-700 uppercase">Lezioni/Sett</th>
                                                <th className="px-6 py-3 text-left text-xs font-semibold text-zinc-700 uppercase">Regolarità</th>
                                                <th className="px-6 py-3 text-left text-xs font-semibold text-zinc-700 uppercase">Ultima</th>
                                                <th className="px-6 py-3 text-left text-xs font-semibold text-zinc-700 uppercase">Status</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-zinc-100">
                                            {statistiche.topClienti.map((cliente, index) => (
                                                <tr key={index} className="hover:bg-zinc-50">
                                                    <td className="px-6 py-4">
                                                        <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full font-bold text-sm ${
                                                            index === 0 ? 'bg-yellow-100 text-yellow-700' :
                                                            index === 1 ? 'bg-zinc-200 text-zinc-700' :
                                                            index === 2 ? 'bg-orange-100 text-orange-700' :
                                                            'bg-blue-50 text-blue-700'
                                                        }`}>
                                                            {index + 1}
                                                        </span>
                                                    </td>
                                                    <td className="px-6 py-4 text-sm font-medium text-zinc-900">{cliente.nome}</td>
                                                    <td className="px-6 py-4 text-sm font-bold text-zinc-900">{cliente.totaleLezioni}</td>
                                                    <td className="px-6 py-4 text-sm text-blue-600 font-semibold">
                                                        {cliente.mediaLezioniSettimana.toFixed(2)}
                                                    </td>
                                                    <td className="px-6 py-4">
                                                        <span className={`px-2 py-1 rounded text-xs font-semibold ${
                                                            cliente.regolaValore === 'Alta' ? 'bg-green-100 text-green-700' :
                                                            cliente.regolaValore === 'Media' ? 'bg-yellow-100 text-yellow-700' :
                                                            'bg-red-100 text-red-700'
                                                        }`}>
                                                            {cliente.regolaValore}
                                                        </span>
                                                    </td>
                                                    <td className="px-6 py-4 text-sm text-zinc-600">
                                                        {cliente.giorniDallUltima === 0 ? 'Oggi' : 
                                                         cliente.giorniDallUltima === 1 ? 'Ieri' : 
                                                         `${cliente.giorniDallUltima}gg fa`}
                                                    </td>
                                                    <td className="px-6 py-4">
                                                        <span className={`px-2 py-1 rounded text-xs font-semibold ${
                                                            cliente.attivo ? 'bg-green-100 text-green-700' : 'bg-zinc-100 text-zinc-700'
                                                        }`}>
                                                            {cliente.attivo ? 'Attivo' : 'Inattivo'}
                                                        </span>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>

                            {/* Insights */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className={`rounded-xl p-6 border ${
                                    statistiche.marginePercentuale >= 50 
                                        ? 'bg-gradient-to-br from-green-50 to-green-100 border-green-200' 
                                        : statistiche.marginePercentuale >= 20
                                            ? 'bg-gradient-to-br from-yellow-50 to-yellow-100 border-yellow-200'
                                            : 'bg-gradient-to-br from-red-50 to-red-100 border-red-200'
                                }`}>
                                    <div className="flex items-center gap-2 mb-2">
                                        <Wallet className={`w-5 h-5 ${
                                            statistiche.marginePercentuale >= 50 ? 'text-green-700' : 
                                            statistiche.marginePercentuale >= 20 ? 'text-yellow-700' : 'text-red-700'
                                        }`} />
                                        <h4 className={`font-semibold ${
                                            statistiche.marginePercentuale >= 50 ? 'text-green-900' : 
                                            statistiche.marginePercentuale >= 20 ? 'text-yellow-900' : 'text-red-900'
                                        }`}>Analisi Economica</h4>
                                    </div>
                                    <p className={`text-sm ${
                                        statistiche.marginePercentuale >= 50 ? 'text-green-800' : 
                                        statistiche.marginePercentuale >= 20 ? 'text-yellow-800' : 'text-red-800'
                                    }`}>
                                        {statistiche.marginePercentuale >= 50 ? (
                                            <>Margine <span className="font-bold">eccellente</span> del {statistiche.marginePercentuale.toFixed(1)}%. Il PT è molto profittevole! 🎉</>
                                        ) : statistiche.marginePercentuale >= 20 ? (
                                            <>Margine <span className="font-bold">buono</span> del {statistiche.marginePercentuale.toFixed(1)}%. Performance nella media.</>
                                        ) : (
                                            <>Margine <span className="font-bold">basso</span> del {statistiche.marginePercentuale.toFixed(1)}%. Considera di ottimizzare i costi o aumentare le tariffe.</>
                                        )}
                                    </p>
                                </div>

                                <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl p-6 border border-purple-200">
                                    <div className="flex items-center gap-2 mb-2">
                                        <Group className="w-5 h-5 text-purple-700" />
                                        <h4 className="font-semibold text-purple-900">Frequenza Clienti</h4>
                                    </div>
                                    <p className="text-sm text-purple-800">
                                        <span className="font-bold">{statistiche.clientiAttivi}</span> clienti attivi con media di <span className="font-bold">{statistiche.mediaFrequenzaGenerale.toFixed(1)}</span> lezioni/settimana. 
                                        {statistiche.clientiAttivi > statistiche.clientiInattivi ? 
                                            <> Ottimo tasso di retention! ✨</> : 
                                            <> Considera strategie per riattivare i clienti inattivi.</>
                                        }
                                    </p>
                                </div>
                            </div>
                        </>
                    )}

                    {!statistiche && lezioniOriginali.length === 0 && (
                        <div className="bg-white rounded-2xl shadow-sm border border-zinc-200 p-12 text-center">
                            <Calendar className="w-16 h-16 text-zinc-400 mx-auto mb-4" />
                            <h3 className="text-xl font-semibold text-zinc-900 mb-2">Nessuna lezione registrata</h3>
                            <p className="text-zinc-600">Questo Personal Trainer non ha ancora lezioni nel sistema.</p>
                        </div>
                    )}

                    <div className="flex gap-3 justify-end">
                        <button 
                            onClick={() => alertDelete()} 
                            className="inline-flex items-center gap-2 px-4 py-2.5 bg-red-600 hover:bg-red-700 text-white font-semibold rounded-xl text-sm shadow-sm hover:shadow transition-all duration-200"
                        >
                            Elimina profilo
                        </button>
                        
                        {(nome !== personalTrainer.nome || cognome !== personalTrainer?.cognome || costo !== personalTrainer?.costo) && (
                            <button 
                                onClick={() => alertPatch()} 
                                className="inline-flex items-center gap-2 px-6 py-2.5 bg-yellow-400 hover:bg-yellow-500 text-zinc-900 font-bold rounded-xl text-sm shadow-sm hover:shadow transition-all duration-200"
                            >
                                Salva Modifiche
                            </button>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}

export default ProfiloPersonalTrainer;
